import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Features from "@/components/Features";
import DashboardMockup from "@/components/DashboardMockup";
import Pricing from "@/components/Pricing";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Play,
  ShieldCheck,
  Sparkles,
  MapPin,
  Star,
  Quote,
  Building2,
  Users,
  TrendingUp,
} from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";
import { resolveCity, TOP_CITIES, CITIES } from "@/data/cities";
import { getCaseStudyByCity } from "@/data/caseStudies";

export default function LocalCityPage() {
  const { city: rawSlug } = useParams();
  const city = resolveCity(rawSlug);
  const { openLeadCapture } = useLeadCapture();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [rawSlug]);

  if (!city) return null;

  const ctaSource = `Lokalt · ${city.name}`;
  const seoTitle = `Hunddagis & hundpensionat system i ${city.name}`;
  const seoDescription = `Hundify är affärssystemet för hunddagis, hundpensionat och hundrastnings­verksamheter i ${city.name}. Smart bokning, automatisk fakturering och digital hundjournal — byggt i Sverige.`;
  const seoPath = `/lokalt/${city.slug}`;
  const caseStudy = getCaseStudyByCity(city.slug);

  return (
    <div
      data-testid={`local-page-${city.slug}`}
      className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
    >
      <Seo title={seoTitle} description={seoDescription} path={seoPath} />
      <Navbar />

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden pt-36 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0 bg-dotted opacity-60 pointer-events-none" />
          <div className="absolute -top-40 -left-40 h-[500px] w-[500px] blob-emerald opacity-70" />

          <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
            {/* Breadcrumb */}
            <nav className="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-1 mb-8">
              <Link to="/" className="hover:text-emerald-600 dark:hover:text-emerald-400">
                Hem
              </Link>
              <span>›</span>
              <span>Lokalt</span>
              <span>›</span>
              <span data-testid="local-breadcrumb-city" className="text-slate-700 dark:text-slate-200">
                {city.name}
              </span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Copy */}
              <div className="lg:col-span-7">
                <div
                  data-testid="local-overline"
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Hundify i {city.name}
                </div>

                <h1
                  data-testid="local-headline"
                  className="font-display mt-6 text-4xl sm:text-5xl lg:text-[60px] leading-[1.04] font-bold tracking-tight text-slate-900 dark:text-white"
                >
                  Det smarta affärssystemet för{" "}
                  <span className="relative inline-block whitespace-nowrap">
                    <span className="relative z-10">hundpensionat</span>
                    <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
                  </span>{" "}
                  och{" "}
                  <span className="relative inline-block whitespace-nowrap">
                    <span className="relative z-10">hunddagis</span>
                    <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
                  </span>{" "}
                  i {city.name}.
                </h1>

                <p
                  data-testid="local-subheadline"
                  className="mt-7 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300"
                >
                  Letar du efter ett modernt <strong className="text-slate-800 dark:text-slate-100">hunddagis system i {city.name}</strong>?
                  Eller ett <strong className="text-slate-800 dark:text-slate-100">hundpensionat system i {city.name}</strong> som hanterar
                  bokningar, fakturor och hundjournaler på ett ställe? Hundify är byggt för svenska
                  hundverksamheter — utvecklat i Sverige, supporterat på svenska och utformat för
                  hur ni faktiskt jobbar i vardagen.
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Button
                    data-testid="local-primary-cta"
                    onClick={() => openLeadCapture(ctaSource)}
                    className="h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)] hover:-translate-y-0.5 transition-all group"
                  >
                    Boka demo för {city.name}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    data-testid="local-secondary-cta"
                    variant="outline"
                    onClick={() => openLeadCapture(`${ctaSource} — Demo`)}
                    className="h-12 px-6 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-white font-semibold text-base hover:-translate-y-0.5 transition-all"
                  >
                    <Play className="mr-2 h-4 w-4 fill-current" />
                    Se hur det fungerar
                  </Button>
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500 dark:text-slate-400">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    Gratis i 14 dagar
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    Lokal support på svenska
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    GDPR-säker, svensk datalagring
                  </div>
                </div>
              </div>

              {/* Local map-style card */}
              <div className="lg:col-span-5">
                <div className="relative rounded-[28px] bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white p-7 shadow-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-dotted opacity-15" />
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-300">
                          {city.region}
                        </div>
                        <div className="font-display text-3xl font-bold mt-1">{city.name}</div>
                      </div>
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 shadow-lg">
                        <MapPin className="h-6 w-6" />
                      </div>
                    </div>

                    <p className="mt-5 text-slate-300 text-sm leading-relaxed">{city.blurb}</p>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                      <Stat
                        icon={Building2}
                        label="Hundverksamheter"
                        value={city.activeBusinesses}
                        testid="local-stat-businesses"
                      />
                      <Stat
                        icon={TrendingUp}
                        label="Avg. tidsbesparing"
                        value="12 h/v"
                        testid="local-stat-savings"
                      />
                    </div>

                    {city.neighborhoods.length > 0 && (
                      <div className="mt-6 pt-5 border-t border-white/10">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                          Vi servar bland annat
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {city.neighborhoods.map((n) => (
                            <span
                              key={n}
                              className="inline-flex items-center text-[12px] font-medium rounded-full bg-white/5 text-slate-200 px-2.5 py-1 border border-white/10"
                            >
                              {n}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DASHBOARD MOCKUP (reused) */}
        <DashboardMockup />

        {/* FEATURES (reused) */}
        <Features />

        {/* LOCAL SOCIAL PROOF */}
        <LocalSocialProof city={city} caseStudy={caseStudy} />

        {/* PRICING (reused) */}
        <Pricing />

        {/* OTHER CITIES */}
        <section className="py-20 border-t border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Hundify i hela Sverige
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
                Driver du en hundverksamhet i en annan stad?
              </h2>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                Hundify används av hundpensionat, hunddagis och hundrastare i hela landet.
                Hitta din stad nedan eller boka en demo — vi hör av oss inom 24 timmar.
              </p>
            </div>
            <div
              data-testid="other-cities-grid"
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              {Object.values(CITIES)
                .filter((c) => c.slug !== city.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/lokalt/${c.slug}`}
                    data-testid={`other-city-link-${c.slug}`}
                    className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display font-semibold text-slate-900 dark:text-white">
                          {c.name}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {c.region}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-white/5 border border-white/10 p-3">
      <div className="flex items-center gap-2 text-emerald-300">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="font-display text-xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}

function LocalSocialProof({ city, caseStudy }) {
  const t = city.testimonial;
  const sparkles = [Sparkles, Users, Building2, Star];
  void sparkles; // not used directly; reserved for future variants

  return (
    <section
      data-testid="local-social-proof"
      className="relative py-24 md:py-28 overflow-hidden"
    >
      <div className="absolute top-1/2 -translate-y-1/2 -right-32 h-[420px] w-[420px] blob-emerald opacity-50 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Quote */}
        <figure
          data-testid="local-testimonial"
          className="lg:col-span-7 relative rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-9 md:p-12 shadow-xl"
        >
          <Quote
            className="absolute -top-5 -left-3 h-12 w-12 text-emerald-500"
            fill="currentColor"
          />

          <div className="flex gap-1">
            {Array.from({ length: t.rating || 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-emerald-500 text-emerald-500" />
            ))}
          </div>

          <blockquote
            data-testid="local-testimonial-quote"
            className="mt-5 font-display text-2xl md:text-3xl leading-snug font-semibold text-slate-900 dark:text-white"
          >
            &ldquo;{t.quote}&rdquo;
          </blockquote>

          <figcaption className="mt-8 flex items-center gap-4 pt-6 border-t border-slate-100 dark:border-slate-800">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white font-display font-bold text-xl flex items-center justify-center shadow-md">
              {t.initial}
            </div>
            <div className="min-w-0 flex-1">
              <div
                data-testid="local-testimonial-name"
                className="font-semibold text-slate-900 dark:text-white"
              >
                {t.name}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {t.role} · {t.business}
              </div>
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 inline-flex items-center gap-1 font-medium">
                <MapPin className="h-3 w-3" /> {t.area}
              </div>
            </div>
            {caseStudy && (
              <Link
                to={`/kunder/${caseStudy.slug}`}
                data-testid="local-read-case-study"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:gap-2 transition-all whitespace-nowrap"
              >
                Läs hela kundstoryn
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </figcaption>
          {caseStudy && (
            <Link
              to={`/kunder/${caseStudy.slug}`}
              data-testid="local-read-case-study-mobile"
              className="sm:hidden mt-4 inline-flex items-center justify-center gap-1 w-full rounded-xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold py-3 text-sm hover:bg-emerald-500/15 transition-colors"
            >
              Läs hela kundstoryn
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </figure>

        {/* Side content */}
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
            Lokal social proof
          </div>
          <h2
            data-testid="local-social-proof-heading"
            className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white"
          >
            Hundverksamheter i {city.name} litar redan på Hundify.
          </h2>
          <p className="mt-5 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
            Vi jobbar nära våra kunder i {city.region} för att se till att systemet faktiskt
            passar er vardag. Vill du veta hur Hundify skulle fungera för just din verksamhet
            i {city.name}? Boka en kort demo — vi skräddarsyr genomgången efter er.
          </p>

          <ul className="mt-7 space-y-3 text-sm text-slate-700 dark:text-slate-200">
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
              Onboarding på svenska, ofta inom 5 dagar.
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
              Vi importerar dina nuvarande bokningar gratis.
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
              Lokal referenskund i {city.name} kan kopplas in vid behov.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
